import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'
import { navLinks } from '@/constants/site'

/**
 * A floating strip: 14px in from the top, 44px tall, so the gallery's own close
 * button (at 74px) always clears it. The container ignores the pointer so the
 * wall underneath can still be panned from anywhere.
 */
export const SiteNav: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const [veiled, setVeiled] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setVeiled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return (): void => window.removeEventListener('scroll', onScroll)
    }, [])

    return (
        <header className='pointer-events-none fixed inset-x-0 top-0 z-50 h-[58px]'>
            <div
                aria-hidden='true'
                className={cn(
                    'absolute inset-x-0 top-0 h-[92px] transition-opacity duration-700',
                    veiled ? 'opacity-100' : 'opacity-0'
                )}
                style={{
                    background:
                        'linear-gradient(to bottom, color-mix(in oklab, var(--rev-ink) 4%, transparent), transparent)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    maskImage: 'linear-gradient(to bottom, #000 55%, transparent)',
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent)'
                }}
            />

            <div
                className={cn(
                    'relative flex h-[44px] w-full items-center justify-between',
                    // the gallery runs to the viewport edge; the editorial pages are capped,
                    // so the nav lines up with whichever grid it is sitting on
                    pathname !== '/' && 'mx-auto max-w-[var(--rev-measure)]'
                )}
                style={{
                    marginTop: '14px',
                    paddingLeft: 'var(--rev-gutter)',
                    paddingRight: 'var(--rev-gutter)'
                }}
            >
                <div className='flex items-baseline gap-[clamp(10px,1.6vw,18px)]'>
                    <Link
                        href='/'
                        aria-label='Reverie, back to the gallery'
                        className='font-display pointer-events-auto text-[color:var(--rev-ink)] uppercase transition-opacity duration-500 hover:opacity-70'
                        style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.34em' }}
                    >
                        Reverie
                    </Link>

                    <span
                        aria-hidden='true'
                        className='hidden h-[11px] w-px bg-[var(--rev-hairline)] sm:block'
                    />

                    <span
                        className='font-editorial hidden text-[color:var(--rev-ink-faint)] sm:block'
                        style={{ fontSize: '14px', lineHeight: 1 }}
                    >
                        imagined light
                    </span>
                </div>

                <nav aria-label='Primary' className='flex items-center gap-[clamp(14px,2.4vw,32px)]'>
                    {navLinks.map(link => {
                        const active = pathname === link.href

                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'rev-label pointer-events-auto relative py-[6px] text-[11.5px] tracking-[0.22em] text-[color:var(--rev-ink)] transition-opacity duration-500',
                                    active ? 'opacity-100' : 'opacity-55 hover:opacity-100',
                                    link.href === '/' && 'hidden sm:inline-block'
                                )}
                            >
                                {link.label}

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'absolute inset-x-0 bottom-0 h-px origin-left bg-[var(--rev-ink)] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                        active ? 'scale-x-100' : 'scale-x-0'
                                    )}
                                />
                            </Link>
                        )
                    })}
                </nav>
            </div>
        </header>
    )
}
